import Link from "next/link";

import { Button } from "@/components/ui/button";
import { ProductCard } from "@/components/blocks/ProductCard";
import { LoadingSkeleton } from "@/components/loading-skeleton";
import {
  getProductImages,
  isPopulatedShop,
  type CustomerProduct,
  type CustomerShopSummary,
} from "@/features/shop/types/customer-shop";
import { formatProductUnitLabel } from "@/features/shop/types";
import { formatShopLocation } from "@/features/shop/shop-location";
import { formatPublicCategoryLabel } from "@/features/public-site/public-site.utils";

type FeaturedProductsSectionProps = {
  products: CustomerProduct[];
  onAddToCart: (product: CustomerProduct) => void;
  isPending: boolean;
  isLoading?: boolean;
};

export function FeaturedProductsSection({
  products,
  onAddToCart,
  isPending,
  isLoading = false,
}: FeaturedProductsSectionProps) {
  return (
    <section id="featured" className="mt-10 space-y-5">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-primary">
            Featured Products
          </p>
          <h2 className="text-3xl font-black tracking-tight text-foreground">
            Discover our carefully selected premium products.
          </h2>
        </div>
        <Button asChild variant="outline" className="hidden rounded-sm border-primary/30 text-primary md:inline-flex">
          <Link href="/shop">View All</Link>
        </Button>
      </div>

      {isLoading ? (
        <LoadingSkeleton
          variant="cards"
          count={4}
          gridClassName="grid gap-4 md:grid-cols-2 xl:grid-cols-4"
        />
      ) : (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {products.map((product) => {
          const shop = isPopulatedShop(product.shopId)
            ? (product.shopId as CustomerShopSummary)
            : null;

          return (
            <ProductCard
              key={product._id}
              variant="storefront"
              name={product.name}
              description={product.description || "Fresh produce."}
              category={formatPublicCategoryLabel(product.category)}
              extraBadge={{
                label: product.quantity > 0 ? "New" : "Sold Out",
                tone: "destructive",
              }}
              imageUrl={getProductImages(product)[0]}
              priceLabel={`KES ${product.price}`}
              unitLabel={`${product.quantity} ${formatProductUnitLabel(product.unit)}`}
              locationLabel={
                shop ? `${shop.name} · ${formatShopLocation(shop)}` : "Local farm shop"
              }
              quantity={1}
              showQuantity={false}
              onAddToCart={() => onAddToCart(product)}
              isPending={isPending}
            />
          );
        })}
      </div>
      )}
    </section>
  );
}
